import { NavigationProp } from '@react-navigation/native';

import { DepositStackParamList } from '@/navigation/types';
import { logger } from '@/utils/logging';

import { Alerts } from '../alerts/components/AlertsProvider';
import { PaymentMethodTypes } from './const';

export const ADD_DEBIT_CARD_LOG_TAG = '[Add Debit Card]';

type Navigation = Omit<NavigationProp<DepositStackParamList>, 'getState'>;

export const MESSAGE_TYPES = {
    SHOW_PICKER: 'showPicker',
    BAD_BIN: 'badBin',
    STATE: 'state',
    DEPOSIT_SUCCESS: 'depositSuccess',
    DEPOSIT_ERROR: 'depositError',
} as const;

export const PAYSAFE_ERROR_TYPES = {
    INIT: 'init',
    GENERIC: 'generic',
    NOT_SUPPORTED: 'notSupported',
} as const;

export type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];
export type PaysafeErrorType = (typeof PAYSAFE_ERROR_TYPES)[keyof typeof PAYSAFE_ERROR_TYPES];

export type DebitCardFormMessage = {
    type: MessageType;
    payload?: unknown;
};

const isDebitCardFormMessage = (value: unknown): value is DebitCardFormMessage =>
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    typeof value.type === 'string' &&
    Object.values<string>(MESSAGE_TYPES).includes(value.type);

// on web this also receives messages that browser extensions post to the page
export const parseDebitCardFormMessage = (data: unknown): DebitCardFormMessage | undefined => {
    if (typeof data !== 'string') {
        return undefined;
    }
    try {
        const parsed: unknown = JSON.parse(data);
        return isDebitCardFormMessage(parsed) ? parsed : undefined;
    } catch {
        return undefined;
    }
};

/**
 * Shows an InfoSheet to the user telling them that their bank does not accept gaming transactions and show an alternative payment method.
 */
export const handleBadBinMessage = (showInfoSheet: Alerts['showInfoSheet'], navigation: Navigation) => {
    showInfoSheet({
        title: 'This bank does not accept gaming transactions',
        description: 'Try using PayPal as an alternative or dismiss and use a different debit card.',
        buttonLabel: 'Deposit with PayPal',
        handlePress: () => {
            navigation.navigate('DepositMethodWebViewModal', {
                selectedPaymentMethodType: PaymentMethodTypes.PaysafePaypal,
            });
        },
        secondaryLabel: 'Dismiss',
    });
};

/**
 * Navigates the user to the VerifyingDebitDeposit screen.
 */
export const handleDepositSuccessMessage = (
    token: string,
    currency: string,
    shouldTrigger3ds: boolean,
    navigation: Navigation
) => {
    navigation.navigate('VerifyingDebitDeposit', {
        shouldTrigger3ds,
        currency,
        payment_token: token,
    });
};

/**
 * Handles Paysafe error messages and navigates the user to the FailedDepositModal screen.
 * Depending on the type of error, it logs the error.
 */
export const handlePaysafeErrorMessage = (
    data: { type: PaysafeErrorType; error: unknown },
    selectedAmount: string,
    navigation: Navigation
) => {
    switch (data.type) {
        case PAYSAFE_ERROR_TYPES.INIT:
            logger.error(ADD_DEBIT_CARD_LOG_TAG, 'error: failed initializing paysafeJS', data.error);
            break;
        case PAYSAFE_ERROR_TYPES.NOT_SUPPORTED:
            logger.error(ADD_DEBIT_CARD_LOG_TAG, 'error: paysafeJS not supported', data.error);
            break;
        default:
            logger.error(ADD_DEBIT_CARD_LOG_TAG, 'error adding debit card', data.error, { selectedAmount });
    }
    navigation.navigate('FailedDepositModal', {
        paymentProvider: PaymentMethodTypes.Paysafe,
    });
};
